import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, Target, Users, Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const StoryPage = () => {
    const navigate = useNavigate();

    const timeline = [
        {
            year: '2023',
            title: 'The Spark',
            description: 'Recovery Room began as a vision born out of a deep awareness of the mental and emotional weight carried by Black men.'
        },
        {
            year: '2024',
            title: 'Launch',
            description: 'In June 2024, during Men\'s Health Month, the vision was brought to life as a monthly gathering.'
        },
        {
            year: '2025',
            title: 'Growing Impact',
            description: 'Established five volunteer committees and expanded programming to reach more brothers across the DMV area.'
        },
        {
            year: 'Future',
            title: 'The Vision',
            description: 'Opening the nation\'s first universal wellness center designed specifically for Black men in Washington, DC.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            Who We Are
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Our Story
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            How Recovery Room came to be and where we're headed
                        </p>
                    </div>

                    {/* Main Story Content */}
                    <section className="mb-20">
                        <Card className="bg-[#4A5240] border-[#5A6350] overflow-hidden">
                            <div className="h-1 bg-[#C9A227]" />
                            <CardContent className="p-8 lg:p-12">
                                <div className="space-y-6 text-[#A0A0A0] leading-relaxed text-lg">
                                    <p>
                                        The Recovery Room (R&R) began as a spark of an idea in 2023—a vision born out of a deep awareness of the mental and emotional weight carried by Black men in their everyday lives. What started as a concept soon flourished into a tangible movement, rooted in the desire to create a space where authenticity, support, and healing could thrive. By June 2024, during Men's Health Month, that idea was brought to life as a monthly gathering—a bold step in our founder's mission to intentionally build a connected network of Black men prioritizing wellness and community.
                                    </p>
                                    <p>
                                        R&R seeks to be more than just a physical venue; we strive to become a sanctuary, a shared commitment, and a call to action. We are a community-driven initiative designed to combat mental fatigue, dismantle isolation, and foster genuine relationships—free of judgment, pretenses, or hidden agendas. At R&R, Black men are invited to show up fully, breathe deeply, and be unapologetically themselves.
                                    </p>
                                    <p>
                                        Each monthly gathering is carefully curated to help participants pause, reflect, and reset. Whether through open conversation, moments of stillness, or collective reflection, these sessions offer a consistent space for growth, healing, and introspection. Our mission is rooted in the belief that true strength lies in community—and that when Black men support each other, transformation happens.
                                    </p>
                                    <p>
                                        R&R's impact extends far beyond its gatherings. The connections built here ripple outward, creating a network of strength, motivation, and encouragement. We are cultivating a culture of wellness and empowerment—one conversation, one relationship, and one breakthrough at a time.
                                    </p>
                                    <p>
                                        In a world that often fails to honor the unique experiences of Black men, R&R stands as a testament to the power of intentional spaces that center their well-being. This is more than a moment—it's our movement toward healing, legacy, and liberation.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Video Section */}
                    <section className="mb-20">
                        <div className="rounded-2xl overflow-hidden shadow-2xl">
                            <video 
                                className="w-full aspect-video object-cover"
                                controls
                            >
                                <source 
                                    src="https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/jlhsrwoq_candid%202.mov" 
                                    type="video/quicktime" 
                                />
                                <source 
                                    src="https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/jlhsrwoq_candid%202.mov" 
                                    type="video/mp4" 
                                />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </section>

                    {/* Call to Action Quote */}
                    <section className="mb-20">
                        <Card className="bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 border-[#C9A227]/30">
                            <CardContent className="p-8 lg:p-12 text-center">
                                <p className="text-xl lg:text-2xl text-white font-['Playfair_Display'] italic leading-relaxed mb-6">
                                    "If you are in search of a space to be seen, heard, and valued—your place is here, and your journey forward begins now."
                                </p>
                                <p className="text-[#A0A0A0] mb-8">
                                    With your committed support, partnership, and donorship, we can continue to expand this work and create even more opportunities for meaningful connection and wellness.
                                </p>
                                <p className="text-2xl text-[#C9A227] font-['Playfair_Display'] font-bold">
                                    Welcome to the Recovery Room.
                                </p>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Mission & Vision */}
                    <section className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-8">
                        <Card className="bg-[#4A5240] border-[#5A6350]">
                            <CardContent className="p-8">
                                <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mb-6">
                                    <Target className="w-7 h-7 text-[#C9A227]" />
                                </div>
                                <h3 className="text-xl font-bold text-white font-['Playfair_Display'] mb-4">Our Mission</h3>
                                <p className="text-[#A0A0A0] leading-relaxed">
                                    To curate safe spaces for Black men to connect, find purpose, and align within community.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#4A5240] border-[#5A6350]">
                            <CardContent className="p-8">
                                <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mb-6">
                                    <Sparkles className="w-7 h-7 text-[#C9A227]" />
                                </div>
                                <h3 className="text-xl font-bold text-white font-['Playfair_Display'] mb-4">Our Vision</h3>
                                <p className="text-[#A0A0A0] leading-relaxed">
                                    To improve the overall mental wellness of Black men, one room and one experience at a time.
                                </p>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Timeline */}
                    <section className="mb-20">
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <Calendar className="w-6 h-6 text-[#C9A227]" />
                            Our Journey
                        </h2>
                        <div className="space-y-6">
                            {timeline.map((item, idx) => (
                                <div key={idx} className="flex gap-6">
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 flex items-center justify-center text-[#C9A227] font-bold">
                                            {item.year}
                                        </div>
                                        {idx < timeline.length - 1 && (
                                            <div className="w-0.5 h-full bg-[#5A6350] mt-2" />
                                        )}
                                    </div>
                                    <Card className="flex-1 bg-[#4A5240] border-[#5A6350]">
                                        <CardContent className="p-6">
                                            <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                                            <p className="text-[#A0A0A0]">{item.description}</p>
                                        </CardContent>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Values */}
                    <section className="mb-20">
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <Heart className="w-6 h-6 text-[#C9A227]" />
                            Our Core Values
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { title: 'Brotherhood', desc: 'Building authentic connections between Black men' },
                                { title: 'Healing', desc: 'Creating space for mental and emotional wellness' },
                                { title: 'Purpose', desc: 'Helping brothers discover and align with their goals' },
                                { title: 'Community', desc: 'Fostering support systems that uplift and empower' },
                                { title: 'Growth & Leadership', desc: 'Encouraging continuous personal development' },
                                { title: 'Authenticity', desc: 'Celebrating the full expression of Black manhood' }
                            ].map((value, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350]">
                                    <CardContent className="p-6 text-center">
                                        <h3 className="text-white font-semibold text-lg mb-2">{value.title}</h3>
                                        <p className="text-[#A0A0A0] text-sm">{value.desc}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Support CTA */}
                    <section>
                        <Card className="bg-[#4A5240] border-[#5A6350]">
                            <CardContent className="p-8 lg:p-12">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                    <div>
                                        <h3 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-4">
                                            Support Our Movement
                                        </h3>
                                        <p className="text-[#A0A0A0] leading-relaxed">
                                            Your support helps us continue to create meaningful spaces for healing, connection, and growth. Join us in building a legacy of wellness for Black men.
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                                        <Button
                                            size="lg"
                                            onClick={() => navigate('/member/register')}
                                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                        >
                                            Become a Member
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                        <Button
                                            size="lg"
                                            variant="outline"
                                            onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=F3BSD37A8WSJQ', '_blank')}
                                            className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-[#3D4435]"
                                        >
                                            <Heart className="w-4 h-4 mr-2" />
                                            Donate
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default StoryPage;
